import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCarts, createCart, updateCart, addCartItem, updateCartItem, deleteCartItem } from '../api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const GUEST_CART_KEY = 'dji_guest_cart';

const loadGuestCart = () => {
  try { return JSON.parse(localStorage.getItem(GUEST_CART_KEY)) || []; }
  catch { return []; }
};
const saveGuestCart = (items) => localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState(null);
  const [items, setItems] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const loadGuest = useCallback(() => {
    setItems(loadGuestCart());
    setCart(null);
  }, []);

  const syncGuestToApi = useCallback(async (apiCart) => {
    const guestItems = loadGuestCart();
    if (!guestItems.length) return;
    setSyncing(true);
    try {
      for (const gi of guestItems) {
        await addCartItem({
          cart: apiCart.id,
          product_id: gi.product.id,
          quantity: gi.quantity,
          unit_price: gi.unit_price,
        });
      }
      localStorage.removeItem(GUEST_CART_KEY);
    } catch {}
    finally { setSyncing(false); }
  }, []);

  useEffect(() => {
    if (user) {
      getCarts().then(async ({ data }) => {
        // Only pick an ACTIVE cart — never a checked_out or abandoned one
        let activeCart = (data.results || []).find((c) => c.status === 'active');
        if (!activeCart) {
          const created = await createCart();
          activeCart = created.data;
        }
        await syncGuestToApi(activeCart);
        // Re-fetch after sync so items are up to date
        const refreshed = await getCarts();
        const refreshedCart = (refreshed.data.results || []).find((c) => c.status === 'active');
        if (refreshedCart) {
          setCart(refreshedCart);
          setItems(refreshedCart.items || []);
        } else {
          // Synced cart got checked out somehow — create a fresh one
          const fresh = await createCart();
          setCart(fresh.data);
          setItems([]);
        }
      }).catch(loadGuest);
    } else {
      loadGuest();
    }
  }, [user, syncGuestToApi, loadGuest]);

  const addItem = async (product, quantity = 1, unitPrice) => {
    const maxStock = product.inventory ?? Infinity;

    if (!user) {
      const guest = loadGuestCart();
      const existing = guest.find((i) => i.product.id === product.id);
      const currentQty = existing ? existing.quantity : 0;
      const newQty = currentQty + quantity;

      // Stock validation for guest cart
      if (newQty > maxStock) {
        const { default: toast } = await import('react-hot-toast');
        toast.error(`Only ${maxStock} item${maxStock !== 1 ? 's' : ''} available in stock`);
        return;
      }

      const updated = existing
        ? guest.map((i) => i.product.id === product.id ? { ...i, quantity: newQty } : i)
        : [...guest, { product, quantity, unit_price: unitPrice, id: Date.now() }];
      saveGuestCart(updated);
      setItems(updated);
      return;
    }

    // Stock validation for logged-in cart
    const existing = items.find((i) => i.product?.id === product.id);
    const currentQty = existing ? existing.quantity : 0;
    const newQty = currentQty + quantity;

    if (newQty > maxStock) {
      const { default: toast } = await import('react-hot-toast');
      toast.error(`Only ${maxStock} item${maxStock !== 1 ? 's' : ''} available in stock`);
      return;
    }

    if (existing) {
      const { data } = await updateCartItem(existing.id, { quantity: newQty });
      setItems((prev) => prev.map((i) => (i.id === data.id ? data : i)));
    } else {
      const { data } = await addCartItem({ cart: cart.id, product_id: product.id, quantity, unit_price: unitPrice });
      setItems((prev) => [...prev, data]);
    }
  };

  const removeItem = async (itemId) => {
    if (!user) {
      const updated = loadGuestCart().filter((i) => i.id !== itemId);
      saveGuestCart(updated);
      setItems(updated);
      return;
    }
    await deleteCartItem(itemId);
    setItems((prev) => prev.filter((i) => i.id !== itemId));
  };

  const updateQty = async (itemId, quantity) => {
    if (quantity < 1) return removeItem(itemId);

    // Find the item to check its stock
    const cartItem = items.find((i) => i.id === itemId);
    const maxStock = cartItem?.product?.inventory ?? Infinity;

    if (quantity > maxStock) {
      const { default: toast } = await import('react-hot-toast');
      toast.error(`Only ${maxStock} item${maxStock !== 1 ? 's' : ''} available in stock`);
      return;
    }

    if (!user) {
      const updated = loadGuestCart().map((i) => i.id === itemId ? { ...i, quantity } : i);
      saveGuestCart(updated);
      setItems(updated);
      return;
    }
    const { data } = await updateCartItem(itemId, { quantity });
    setItems((prev) => prev.map((i) => (i.id === data.id ? data : i)));
  };

  // Called after a successful order — marks cart as checked_out on the server
  // so a hard refresh never reloads the old items
  const clearCart = async () => {
    localStorage.removeItem(GUEST_CART_KEY);
    if (cart?.id) {
      try {
        await updateCart(cart.id, { status: 'checked_out' });
      } catch {
        // If PATCH fails (e.g. already checked out), still clear locally
      }
    }
    setItems([]);
    setCart(null);
  };

  const total = items.reduce((sum, i) => sum + parseFloat(i.unit_price || 0) * i.quantity, 0);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, items, addItem, removeItem, updateQty, clearCart, total, count, syncing }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);